/**
 * themeEngine.js — comprehensive theme definitions for LinkLane bio pages.
 *
 * Each theme carries:
 *   - All original flat props (backward-compat with BioEditor / ThemeCard / PhonePreview)
 *   - motion  — animation personality
 *   - background — descriptor for BioBackground renderer
 *   - tokens  — CSS custom property values (applied as inline style on the root element)
 */

/**
 * bgInlineStyle — convert a theme's bgStyle to a safe inline style object.
 *
 * The CSS `background` shorthand, when set via inline style, resets ALL background
 * sub-properties — including `background-size` — to their initial values. This
 * breaks animated themes whose CSS classes (.bio-animated-gradient, .bio-aurora-bg)
 * depend on `background-size: 300% 300%` being in effect.
 *
 * Fix: use `backgroundImage` (non-shorthand) so the CSS class can freely own
 * `background-size` and `background-position`.
 *
 * @param {object} bgStyle   — theme.bgStyle  (may contain { background: "..." })
 * @param {string} fontFamily — theme.fontFamily (optional, added to the returned style)
 * @returns {React.CSSProperties}
 */
export function bgInlineStyle(bgStyle, fontFamily) {
  const { background, ...rest } = bgStyle || {};
  return {
    ...(fontFamily ? { fontFamily } : {}),
    ...(background ? { backgroundImage: background } : {}),
    ...rest,
  };
}

/**
 * effectiveTheme — merge user customization over a base theme.
 * custom: { accent?, font?, bgColor?, buttonRadius? } (any subset, or null).
 * Overrides are applied as inline styles so they win over the theme's Tailwind classes.
 */
export function effectiveTheme(base, custom) {
  if (!base || !custom || typeof custom !== "object") return base;
  const t = { ...base };
  if (custom.font) {
    t.fontFamily = custom.font;
  }
  if (custom.accent) {
    t.accent = custom.accent;
    t.previewAccent = custom.accent;
  }
  if (custom.bgColor) {
    // Drop the gradient/shorthand + class background, force a solid color.
    const { background, ...restBg } = base.bgStyle || {};
    t.bgStyle = { ...restBg, backgroundColor: custom.bgColor };
    t.bg = "";
    t.bgAnimClass = "";
  }
  if (custom.buttonRadius) {
    t.buttonShape = "";
    t.cardStyle = { ...(base.cardStyle || {}), borderRadius: custom.buttonRadius };
  }
  return t;
}

export const THEME_TIERS = {
  free: { label: "FREE", color: "bg-mint/20 text-[#065F46] border-mint/40" },
  pro: { label: "PRO", color: "bg-candy/15 text-[#5B21B6] border-candy/30" },
  enterprise: { label: "ENT", color: "bg-yellow/20 text-[#78350F] border-yellow/40" },
};

export const THEME_ENGINE = {
  // ── FREE ──────────────────────────────────────────────────────────────────

  minimal: {
    // ── flat (backward compat) ──
    label: "Minimal",
    tier: "free",
    fontFamily: "'Plus Jakarta Sans', sans-serif",
    bg: "bg-white",
    bgStyle: {},
    bgAnimClass: "",
    text: "text-[#1E293B]",
    subtext: "text-[#1E293B]/60",
    card: "bg-white border-2 border-[#1E293B] font-semibold text-[#1E293B] hover:shadow-none hover:translate-x-[2px] hover:translate-y-[2px]",
    cardStyle: { boxShadow: "3px 3px 0 0 #1E293B" },
    accent: "#1E293B",
    buttonShape: "rounded-xl",
    previewBg: "#ffffff",
    previewAccent: "#1E293B",
    previewBtn: "#f1f5f9",
    previewBtnBorder: "#1E293B",

    // ── engine ──
    motion: {
      preset: "minimal",
      cardHover: "press",
      pageEnter: "fade",
      reducedOk: true,
    },
    background: {
      type: "solid",
      base: "#ffffff",
    },
    tokens: {
      "--bio-bg": "#ffffff",
      "--bio-text": "#1E293B",
      "--bio-subtext": "rgba(30,41,59,0.6)",
      "--bio-accent": "#1E293B",
      "--bio-surface": "#f1f5f9",
      "--bio-border": "#1E293B",
      "--bio-radius": "12px",
      "--bio-font": "'Plus Jakarta Sans', sans-serif",
    },
  },

  dark: {
    label: "Dark",
    tier: "free",
    fontFamily: "'Plus Jakarta Sans', sans-serif",
    bg: "bg-[#1E293B]",
    bgStyle: {},
    bgAnimClass: "",
    text: "text-white",
    subtext: "text-white/60",
    card: "bg-white/10 border border-white/20 font-semibold text-white hover:bg-white/20",
    cardStyle: {},
    accent: "#34D399",
    buttonShape: "rounded-xl",
    previewBg: "#1E293B",
    previewAccent: "#34D399",
    previewBtn: "rgba(255,255,255,0.12)",
    previewBtnBorder: "rgba(255,255,255,0.2)",

    motion: {
      preset: "calm",
      cardHover: "lift",
      pageEnter: "fade",
      reducedOk: true,
    },
    background: {
      type: "solid",
      base: "#1E293B",
    },
    tokens: {
      "--bio-bg": "#1E293B",
      "--bio-text": "#ffffff",
      "--bio-subtext": "rgba(255,255,255,0.6)",
      "--bio-accent": "#34D399",
      "--bio-surface": "rgba(255,255,255,0.1)",
      "--bio-border": "rgba(255,255,255,0.2)",
      "--bio-radius": "12px",
      "--bio-font": "'Plus Jakarta Sans', sans-serif",
    },
  },

  warm: {
    label: "Warm",
    tier: "free",
    fontFamily: "'Lora', Georgia, serif",
    bg: "bg-[#FEF9F0]",
    bgStyle: {},
    bgAnimClass: "",
    text: "text-[#7C3F1E]",
    subtext: "text-[#7C3F1E]/60",
    card: "bg-[#FDEBD0] border-2 border-[#C2713A]/40 font-semibold text-[#7C3F1E] hover:bg-[#FBDAB8]",
    cardStyle: {},
    accent: "#C2713A",
    buttonShape: "rounded-2xl",
    previewBg: "#FEF9F0",
    previewAccent: "#C2713A",
    previewBtn: "#FDEBD0",
    previewBtnBorder: "#C2713A",

    motion: {
      preset: "float",
      cardHover: "float",
      pageEnter: "slide-up",
      reducedOk: true,
    },
    background: {
      type: "solid",
      base: "#FEF9F0",
    },
    tokens: {
      "--bio-bg": "#FEF9F0",
      "--bio-text": "#7C3F1E",
      "--bio-subtext": "rgba(124,63,30,0.6)",
      "--bio-accent": "#C2713A",
      "--bio-surface": "#FDEBD0",
      "--bio-border": "rgba(194,113,58,0.4)",
      "--bio-radius": "16px",
      "--bio-font": "'Lora', Georgia, serif",
    },
  },

  fresh: {
    label: "Fresh",
    tier: "free",
    fontFamily: "'Nunito', 'Plus Jakarta Sans', sans-serif",
    bg: "bg-[#F0FDF4]",
    bgStyle: {},
    bgAnimClass: "",
    text: "text-[#14532D]",
    subtext: "text-[#14532D]/60",
    card: "bg-[#DCFCE7] border-2 border-[#16A34A]/30 font-semibold text-[#14532D] hover:bg-[#BBF7D0]",
    cardStyle: {},
    accent: "#16A34A",
    buttonShape: "rounded-full",
    previewBg: "#F0FDF4",
    previewAccent: "#16A34A",
    previewBtn: "#DCFCE7",
    previewBtnBorder: "#16A34A",

    motion: {
      preset: "calm",
      cardHover: "press",
      pageEnter: "fade",
      reducedOk: true,
    },
    background: {
      type: "solid",
      base: "#F0FDF4",
    },
    tokens: {
      "--bio-bg": "#F0FDF4",
      "--bio-text": "#14532D",
      "--bio-subtext": "rgba(20,83,45,0.6)",
      "--bio-accent": "#16A34A",
      "--bio-surface": "#DCFCE7",
      "--bio-border": "rgba(22,163,74,0.3)",
      "--bio-radius": "9999px",
      "--bio-font": "'Nunito', 'Plus Jakarta Sans', sans-serif",
    },
  },

  // ── PRO ───────────────────────────────────────────────────────────────────

  candy: {
    label: "Candy",
    tier: "pro",
    fontFamily: "'Outfit', sans-serif",
    bg: "bg-[#F3EEFF]",
    bgStyle: {},
    bgAnimClass: "",
    text: "text-[#1E293B]",
    subtext: "text-[#1E293B]/60",
    card: "bg-white border-2 border-[#8B5CF6] font-semibold text-[#8B5CF6] hover:shadow-none hover:translate-x-[2px] hover:translate-y-[2px]",
    cardStyle: { boxShadow: "4px 4px 0 0 #8B5CF6" },
    accent: "#8B5CF6",
    buttonShape: "rounded-xl",
    previewBg: "#F3EEFF",
    previewAccent: "#8B5CF6",
    previewBtn: "#ffffff",
    previewBtnBorder: "#8B5CF6",

    motion: {
      preset: "pulse",
      cardHover: "lift",
      pageEnter: "slide-up",
      reducedOk: true,
    },
    background: {
      type: "solid",
      base: "#F3EEFF",
    },
    tokens: {
      "--bio-bg": "#F3EEFF",
      "--bio-text": "#1E293B",
      "--bio-subtext": "rgba(30,41,59,0.6)",
      "--bio-accent": "#8B5CF6",
      "--bio-surface": "#ffffff",
      "--bio-border": "#8B5CF6",
      "--bio-radius": "12px",
      "--bio-font": "'Outfit', sans-serif",
    },
  },

  sunset: {
    label: "Sunset",
    tier: "pro",
    fontFamily: "'Poppins', sans-serif",
    bg: "bg-white",
    bgStyle: { background: "linear-gradient(160deg, #FFF3E0 0%, #FCE4EC 50%, #F3E5F5 100%)" },
    bgAnimClass: "bio-animated-gradient",
    text: "text-[#7B1FA2]",
    subtext: "text-[#7B1FA2]/60",
    card: "bg-white/70 backdrop-blur-sm border border-[#CE93D8]/60 font-semibold text-[#7B1FA2] hover:bg-white/90",
    cardStyle: {},
    accent: "#F06292",
    buttonShape: "rounded-full",
    previewBg: "#fce4ec",
    previewAccent: "#F06292",
    previewBtn: "rgba(255,255,255,0.7)",
    previewBtnBorder: "#CE93D8",
    previewBgGradient: "linear-gradient(160deg, #FFF3E0, #FCE4EC, #F3E5F5)",

    motion: {
      preset: "aurora",
      cardHover: "float",
      pageEnter: "slide-up",
      reducedOk: true,
    },
    background: {
      type: "animated-gradient",
      base: "#FCE4EC",
    },
    tokens: {
      "--bio-bg": "linear-gradient(160deg, #FFF3E0, #FCE4EC, #F3E5F5)",
      "--bio-text": "#7B1FA2",
      "--bio-subtext": "rgba(123,31,162,0.6)",
      "--bio-accent": "#F06292",
      "--bio-surface": "rgba(255,255,255,0.7)",
      "--bio-border": "rgba(206,147,216,0.6)",
      "--bio-radius": "9999px",
      "--bio-font": "'Poppins', sans-serif",
    },
  },

  neon: {
    label: "Neon",
    tier: "pro",
    fontFamily: "'Space Grotesk', monospace",
    bg: "bg-[#050A0E]",
    bgStyle: {},
    bgAnimClass: "",
    text: "text-white",
    subtext: "text-white/50",
    card: "bg-transparent border-2 border-[#00FF88]/60 font-semibold text-[#00FF88] hover:bg-[#00FF88]/10 hover:border-[#00FF88]",
    cardStyle: {},
    accent: "#00FF88",
    buttonShape: "rounded-lg",
    previewBg: "#050A0E",
    previewAccent: "#00FF88",
    previewBtn: "transparent",
    previewBtnBorder: "#00FF88",

    motion: {
      preset: "neon",
      cardHover: "neon",
      pageEnter: "fade",
      reducedOk: false,
    },
    background: {
      type: "orbs",
      base: "#050A0E",
      orbs: [
        {
          x: "5%",
          y: "20%",
          size: 300,
          color: "radial-gradient(circle, rgba(0,255,136,0.2) 0%, transparent 70%)",
          blur: 60,
          opacity: 0.8,
          duration: 9,
          delay: 0,
        },
        {
          x: "60%",
          y: "60%",
          size: 200,
          color: "radial-gradient(circle, rgba(0,200,255,0.15) 0%, transparent 70%)",
          blur: 50,
          opacity: 0.6,
          duration: 11,
          delay: 2,
        },
        {
          x: "80%",
          y: "10%",
          size: 160,
          color: "radial-gradient(circle, rgba(0,255,136,0.1) 0%, transparent 70%)",
          blur: 40,
          opacity: 0.5,
          duration: 7,
          delay: 4,
        },
      ],
    },
    tokens: {
      "--bio-bg": "#050A0E",
      "--bio-text": "#ffffff",
      "--bio-subtext": "rgba(255,255,255,0.5)",
      "--bio-accent": "#00FF88",
      "--bio-surface": "transparent",
      "--bio-border": "rgba(0,255,136,0.6)",
      "--bio-radius": "8px",
      "--bio-font": "'Space Grotesk', monospace",
    },
  },

  rose: {
    label: "Rose",
    tier: "pro",
    fontFamily: "'DM Serif Display', Georgia, serif",
    bg: "bg-[#FFF0F3]",
    bgStyle: {},
    bgAnimClass: "",
    text: "text-[#881337]",
    subtext: "text-[#881337]/60",
    card: "bg-[#FFE4E8] border-2 border-[#F43F5E]/40 font-semibold text-[#BE123C] hover:bg-[#FECDD3]",
    cardStyle: {},
    accent: "#F43F5E",
    buttonShape: "rounded-full",
    previewBg: "#FFF0F3",
    previewAccent: "#F43F5E",
    previewBtn: "#FFE4E8",
    previewBtnBorder: "#F43F5E",

    motion: {
      preset: "float",
      cardHover: "float",
      pageEnter: "slide-up",
      reducedOk: true,
    },
    background: {
      type: "solid",
      base: "#FFF0F3",
    },
    tokens: {
      "--bio-bg": "#FFF0F3",
      "--bio-text": "#881337",
      "--bio-subtext": "rgba(136,19,55,0.6)",
      "--bio-accent": "#F43F5E",
      "--bio-surface": "#FFE4E8",
      "--bio-border": "rgba(244,63,94,0.4)",
      "--bio-radius": "9999px",
      "--bio-font": "'DM Serif Display', Georgia, serif",
    },
  },

  ocean: {
    label: "Ocean",
    tier: "pro",
    fontFamily: "'Space Grotesk', sans-serif",
    bg: "bg-white",
    bgStyle: { background: "linear-gradient(180deg, #0F172A 0%, #0E3A6A 50%, #065F6A 100%)" },
    bgAnimClass: "",
    text: "text-white",
    subtext: "text-white/60",
    card: "bg-white/10 backdrop-blur-sm border border-[#38BDF8]/40 font-semibold text-[#7DD3FC] hover:bg-white/20 hover:border-[#38BDF8]/70",
    cardStyle: {},
    accent: "#38BDF8",
    buttonShape: "rounded-xl",
    previewBg: "#0F172A",
    previewAccent: "#38BDF8",
    previewBtn: "rgba(255,255,255,0.1)",
    previewBtnBorder: "rgba(56,189,248,0.5)",
    previewBgGradient: "linear-gradient(180deg, #0F172A, #0E3A6A, #065F6A)",

    motion: {
      preset: "float",
      cardHover: "lift",
      pageEnter: "slide-up",
      reducedOk: true,
    },
    background: {
      type: "gradient",
      base: "#0F172A",
    },
    tokens: {
      "--bio-bg": "linear-gradient(180deg, #0F172A 0%, #0E3A6A 50%, #065F6A 100%)",
      "--bio-text": "#ffffff",
      "--bio-subtext": "rgba(255,255,255,0.6)",
      "--bio-accent": "#38BDF8",
      "--bio-surface": "rgba(255,255,255,0.1)",
      "--bio-border": "rgba(56,189,248,0.4)",
      "--bio-radius": "12px",
      "--bio-font": "'Space Grotesk', sans-serif",
    },
  },

  midnight: {
    label: "Midnight",
    tier: "pro",
    fontFamily: "'Playfair Display', Georgia, serif",
    bg: "bg-[#0A0A0F]",
    bgStyle: {},
    bgAnimClass: "",
    text: "text-white",
    subtext: "text-white/50",
    card: "bg-[#1C1C28] border border-[#F59E0B]/40 font-semibold text-[#F59E0B] hover:bg-[#F59E0B]/10 hover:border-[#F59E0B]/70",
    cardStyle: {},
    accent: "#F59E0B",
    buttonShape: "rounded-lg",
    previewBg: "#0A0A0F",
    previewAccent: "#F59E0B",
    previewBtn: "#1C1C28",
    previewBtnBorder: "#F59E0B",

    motion: {
      preset: "calm",
      cardHover: "glow",
      pageEnter: "fade",
      reducedOk: true,
    },
    background: {
      type: "solid",
      base: "#0A0A0F",
    },
    tokens: {
      "--bio-bg": "#0A0A0F",
      "--bio-text": "#ffffff",
      "--bio-subtext": "rgba(255,255,255,0.5)",
      "--bio-accent": "#F59E0B",
      "--bio-surface": "#1C1C28",
      "--bio-border": "rgba(245,158,11,0.4)",
      "--bio-radius": "8px",
      "--bio-font": "'Playfair Display', Georgia, serif",
    },
  },

  // ── ENTERPRISE ────────────────────────────────────────────────────────────

  aurora: {
    label: "Aurora",
    tier: "pro",
    fontFamily: "'Space Grotesk', sans-serif",
    bg: "bg-white",
    bgStyle: {
      background: "linear-gradient(135deg, #0D0D1A, #2D1654, #0A2E4A, #0A3A2A, #1A2E0A, #0D0D1A)",
    },
    bgAnimClass: "bio-aurora-bg",
    text: "text-white",
    subtext: "text-white/60",
    card: "bg-white/10 backdrop-blur-md border border-white/20 font-semibold text-white hover:bg-white/20 hover:border-white/40",
    cardStyle: {
      background:
        "linear-gradient(135deg, rgba(139,92,246,0.25), rgba(59,130,246,0.2), rgba(16,185,129,0.2))",
    },
    accent: "#A78BFA",
    buttonShape: "rounded-xl",
    previewBg: "#1A0A2E",
    previewAccent: "#A78BFA",
    previewBtn: "rgba(255,255,255,0.1)",
    previewBtnBorder: "rgba(255,255,255,0.25)",
    previewBgGradient: "linear-gradient(135deg, #0D0D1A, #2D1654, #0A2E4A)",

    motion: {
      preset: "aurora",
      cardHover: "lift",
      pageEnter: "fade",
      reducedOk: false,
    },
    background: {
      type: "orbs",
      base: "#0D0D1A",
      orbs: [
        {
          x: "15%",
          y: "10%",
          size: 350,
          color: "radial-gradient(circle, rgba(139,92,246,0.25) 0%, transparent 70%)",
          blur: 80,
          opacity: 0.7,
          duration: 11,
          delay: 0,
        },
        {
          x: "65%",
          y: "25%",
          size: 280,
          color: "radial-gradient(circle, rgba(59,130,246,0.2) 0%, transparent 70%)",
          blur: 70,
          opacity: 0.6,
          duration: 14,
          delay: 3,
        },
        {
          x: "30%",
          y: "65%",
          size: 240,
          color: "radial-gradient(circle, rgba(16,185,129,0.18) 0%, transparent 70%)",
          blur: 60,
          opacity: 0.55,
          duration: 9,
          delay: 1,
        },
        {
          x: "75%",
          y: "70%",
          size: 200,
          color: "radial-gradient(circle, rgba(167,139,250,0.15) 0%, transparent 70%)",
          blur: 50,
          opacity: 0.5,
          duration: 12,
          delay: 5,
        },
      ],
    },
    tokens: {
      "--bio-bg": "linear-gradient(135deg, #0D0D1A, #2D1654, #0A2E4A, #0A3A2A)",
      "--bio-text": "#ffffff",
      "--bio-subtext": "rgba(255,255,255,0.6)",
      "--bio-accent": "#A78BFA",
      "--bio-surface": "rgba(255,255,255,0.1)",
      "--bio-border": "rgba(255,255,255,0.2)",
      "--bio-radius": "12px",
      "--bio-font": "'Space Grotesk', sans-serif",
    },
  },

  glass: {
    label: "Glass",
    tier: "pro",
    fontFamily: "'Poppins', sans-serif",
    bg: "bg-white",
    bgStyle: {
      background: "linear-gradient(135deg, #E0E7FF 0%, #F0F9FF 40%, #EFF6FF 70%, #EDE9FE 100%)",
    },
    bgAnimClass: "bio-animated-gradient",
    text: "text-[#1E293B]",
    subtext: "text-[#1E293B]/60",
    card: "bg-white/60 backdrop-blur-xl border border-white/80 font-semibold text-[#1E293B] hover:bg-white/80",
    cardStyle: { boxShadow: "0 4px 24px rgba(99,102,241,0.12)" },
    accent: "#6366F1",
    buttonShape: "rounded-2xl",
    previewBg: "#E0E7FF",
    previewAccent: "#6366F1",
    previewBtn: "rgba(255,255,255,0.6)",
    previewBtnBorder: "rgba(255,255,255,0.9)",
    previewBgGradient: "linear-gradient(135deg, #E0E7FF, #F0F9FF, #EDE9FE)",

    motion: {
      preset: "glass",
      cardHover: "lift",
      pageEnter: "slide-up",
      reducedOk: true,
    },
    background: {
      type: "animated-gradient",
      base: "#E0E7FF",
    },
    tokens: {
      "--bio-bg": "linear-gradient(135deg, #E0E7FF, #F0F9FF, #EFF6FF, #EDE9FE)",
      "--bio-text": "#1E293B",
      "--bio-subtext": "rgba(30,41,59,0.6)",
      "--bio-accent": "#6366F1",
      "--bio-surface": "rgba(255,255,255,0.6)",
      "--bio-border": "rgba(255,255,255,0.8)",
      "--bio-radius": "16px",
      "--bio-font": "'Poppins', sans-serif",
    },
  },
};
